import React, { useEffect, useRef } from 'react';
import { PersonPreview } from '../../../ComboBox';
import { ContactPerson } from '../../../../../../../_lib/components/ContactPerson';
import { ISonURL } from '../../../../../../../types/IUrl';
import { useSonTranslation } from '../../../../../../../translation/useSonTranslation';
import { useHtmlValidId } from '../../../../../../../hooks/useHtmlValidId';
import { DragAndDropProvider, moveElementInList } from '../../../../../../../_lib/DragAndDrop';
import { FormsComboboxDraggableMultiSelectListElement } from '../FormsComboboxDraggableMultiSelectListElement';
import { SelectList } from '../../../../../../../_lib/Form2/elements/SelectList/SelectList';

const MODIFIER_HIGHLIGHTING = 'forms-combobox-multiselect__list-item--last-selected';
const ANIMATION_NAME = 'combobox-multiselect-focus-last-selected-item';

type SelectedResult = {
  orgUnit: string;
  emailLink: ISonURL;
  phoneLink: ISonURL;
} & ContactPerson;

type PersonCollectionMultiSelectResultListType = {
  lastSelectedItem?: string; // will look like "id-randomNumber"
  hasSelectedResults: boolean;
  selectedResults: SelectedResult[];
  removeParameterById: (selectedResult: SelectedResult) => void;
  onListChange: (list: SelectedResult[]) => void;
};

export function PersonCollectionMultiSelectResultList({
  hasSelectedResults,
  selectedResults = [],
  lastSelectedItem = '',
  removeParameterById,
  onListChange,
}: PersonCollectionMultiSelectResultListType) {
  const ariaControlsId = useHtmlValidId();
  const prevLastSelectedItemRef = useRef('');
  const lastSelectedItemRef = useRef<HTMLElement>(null);

  const { t } = useSonTranslation('FormsComboBoxMultiSelect');

  const highlightLastSelectedItem = (prevLastSelectedItem: string) => {
    if (lastSelectedItemRef.current && prevLastSelectedItem !== lastSelectedItem) {
      lastSelectedItemRef.current.classList.add(MODIFIER_HIGHLIGHTING);
    }
  };

  const moveElement = (sourceId: string | null | undefined, targetId: string | null | undefined) => {
    onListChange(moveElementInList(selectedResults, sourceId, targetId));
  };

  useEffect(() => {
    prevLastSelectedItemRef.current = lastSelectedItem;
  });

  useEffect(() => {
    if (prevLastSelectedItemRef.current) {
      highlightLastSelectedItem(prevLastSelectedItemRef.current);
    }
  }, [lastSelectedItem]);

  const removeModifierOnAnimationEnd = (event: React.AnimationEvent<HTMLLIElement>) => {
    if (event.animationName === ANIMATION_NAME) {
      if (event.target instanceof HTMLLIElement) {
        event.target.classList.remove(MODIFIER_HIGHLIGHTING);
      }
    }
  };

  /**
   * returns the ref if the lastSelectedItem id is equal to the name.
   */
  const setLastSelectedRef = (selectedResult: SelectedResult) => {
    return selectedResult.id === lastSelectedItem.split('-')[0] ? lastSelectedItemRef : null;
  };

  if (!hasSelectedResults) {
    return null;
  }

  return (
    <DragAndDropProvider>
      <SelectList
        id={ariaControlsId}
        shouldHaveSkipLinks={true}
        headline={t('resultListPersons.selectionHeadline')}
        headlineLevel={4}
        hiddenHeadline={true}
      >
        {selectedResults.map((selectedResult, index) => (
          <FormsComboboxDraggableMultiSelectListElement
            id={selectedResult.id}
            key={selectedResult.id}
            ref={setLastSelectedRef(selectedResult)}
            moveItem={moveElement}
            dragItemPreview={PersonPreview}
            dragItemPreviewProps={{
              emailLink: selectedResult.emailLink,
              orgUnit: selectedResult.orgUnit,
              profileLink: selectedResult.profileLink,
              profileImageUrl: selectedResult.profileImageUrl,
              phoneLink: selectedResult.phoneLink,
            }}
            onAnimationEnd={event => removeModifierOnAnimationEnd(event)}
          >
            <input type="hidden" name={`persons[${index}].id`} value={selectedResult.id} />
            <PersonPreview
              id={selectedResult.id}
              emailLink={selectedResult.emailLink}
              orgUnit={selectedResult.orgUnit}
              profileLink={selectedResult.profileLink}
              profileImageUrl={selectedResult.profileImageUrl}
              phoneLink={selectedResult.phoneLink}
              reset={() => {
                removeParameterById(selectedResult);
              }}
              moveUp={selectedResults.length > 1 && index - 1 >= 0
                ? () => {
                    moveElement(selectedResult.id, selectedResults.at(index - 1)?.id);
                  }
                : undefined}
              moveDown={selectedResults.length > 1 && index + 1 < selectedResults.length
                ? () => {
                    moveElement(selectedResult.id, selectedResults.at(index + 1)?.id);
                  }
                : undefined}
            />
          </FormsComboboxDraggableMultiSelectListElement>
        ))}
      </SelectList>
    </DragAndDropProvider>
  );
}
